import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface RailingSystem {
  id: string;
  title: string;
  image_path: string;
  description: string;
  sort_order: number;
  category: string;
}

export interface HardwareItem {
  id: string;
  category: string;
  title: string;
  image_path: string;
  description: string;
  sort_order: number;
}

export interface PortfolioItem {
  id: string;
  title: string;
  image_path: string;
  description: string;
  sort_order: number;
}

export interface GlassProduct {
  id: string;
  title: string;
  image_path: string;
  description: string;
  sort_order: number;
}

export interface SiteImage {
  id: string;
  key: string;
  image_path: string;
}

type LoadingState = 'idle' | 'loading' | 'loaded' | 'error';

export function useRailingSystems() {
  const [data, setData] = useState<RailingSystem[]>([]);
  const [state, setState] = useState<LoadingState>('idle');

  useEffect(() => {
    setState('loading');
    supabase
      .from('railing_systems')
      .select('*')
      .eq('is_active', true)
      .order('sort_order')
      .then(({ data: rows, error }) => {
        if (error) {
          console.error('Error fetching railing systems:', error);
          setState('error');
        } else {
          setData(rows || []);
          setState('loaded');
        }
      });
  }, []);

  return { data, isLoading: state === 'loading', isLoaded: state === 'loaded', error: state === 'error' };
}

export function useHardwareItems() {
  const [data, setData] = useState<HardwareItem[]>([]);
  const [state, setState] = useState<LoadingState>('idle');

  useEffect(() => {
    setState('loading');
    supabase
      .from('hardware_items')
      .select('*')
      .eq('is_active', true)
      .order('sort_order')
      .then(({ data: rows, error }) => {
        if (error) {
          console.error('Error fetching hardware items:', error);
          setState('error');
        } else {
          setData(rows || []);
          setState('loaded');
        }
      });
  }, []);

  return { data, isLoading: state === 'loading', isLoaded: state === 'loaded', error: state === 'error' };
}

export function usePortfolioItems() {
  const [data, setData] = useState<PortfolioItem[]>([]);
  const [state, setState] = useState<LoadingState>('idle');

  useEffect(() => {
    setState('loading');
    supabase
      .from('portfolio_items')
      .select('*')
      .eq('is_active', true)
      .order('sort_order')
      .then(({ data: rows, error }) => {
        if (error) {
          console.error('Error fetching portfolio items:', error);
          setState('error');
        } else {
          setData(rows || []);
          setState('loaded');
        }
      });
  }, []);

  return { data, isLoading: state === 'loading', isLoaded: state === 'loaded', error: state === 'error' };
}

export function useGlassProducts() {
  const [data, setData] = useState<GlassProduct[]>([]);
  const [state, setState] = useState<LoadingState>('idle');

  useEffect(() => {
    setState('loading');
    supabase
      .from('glass_products')
      .select('*')
      .eq('is_active', true)
      .order('sort_order')
      .then(({ data: rows, error }) => {
        if (error) {
          console.error('Error fetching glass products:', error);
          setState('error');
        } else {
          setData(rows || []);
          setState('loaded');
        }
      });
  }, []);

  return { data, isLoading: state === 'loading', isLoaded: state === 'loaded', error: state === 'error' };
}

export function useSiteImage(key: string) {
  const [data, setData] = useState<SiteImage | null>(null);
  const [state, setState] = useState<LoadingState>('idle');

  useEffect(() => {
    setState('loading');
    supabase
      .from('site_images')
      .select('*')
      .eq('key', key)
      .maybeSingle()
      .then(({ data: row, error }) => {
        if (error) {
          console.error('Error fetching site image:', error);
          setState('error');
        } else {
          setData(row);
          setState('loaded');
        }
      });
  }, [key]);

  return { data, isLoading: state === 'loading', isLoaded: state === 'loaded', error: state === 'error' };
}
